import { useEffect } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Tiny shared primitives for the documents UI: inline SVG icons, status tags,
 * emerald pill tabs, notices and the modal shell.
 *
 * ICONS ARE HAND-ROLLED ON PURPOSE. This package adds zero dependencies, and
 * `lucide-react` sits on wildly different majors in the two consuming apps
 * (super-admin-ui 0.294, platform-app 0.561), so an icon name present in one
 * may be missing or renamed in the other. Inline SVG compiles identically.
 *
 * PALETTE RULE — read before adding a class. These components render in two
 * apps with two different Tailwind setups:
 *   • super-admin-ui  — tailwind.config.js, `dark` 900/800/700/600/500 + `admin`
 *   • platform-app    — cdn.tailwindcss.com, `dark` 900/800/700 ONLY, no `admin`
 * So only `dark-900`, `dark-800` and `dark-700` may be used. Anything darker or
 * lighter comes from a DEFAULT palette that survives in both (zinc for chrome,
 * stone for text, emerald/amber/red/blue/violet for state). `border-dark-600`
 * would silently render borderless in platform-app.
 *
 * Everything here is hooks-only, so it compiles under React 18.2 and 19.2 alike.
 */

/* ── icons ──────────────────────────────────────────────────────────────── */

export interface IconProps {
  size?: number;
  className?: string;
}

const svg = (paths: ReactNode) =>
  function IconImpl({ size = 16, className = '' }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
      >
        {paths}
      </svg>
    );
  };

export const IconGrip = svg(
  <>
    <circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" />
    <circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" />
  </>,
);
export const IconPlus = svg(<><path d="M12 5v14" /><path d="M5 12h14" /></>);
export const IconTrash = svg(
  <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></>,
);
export const IconUp = svg(<path d="M18 15l-6-6-6 6" />);
export const IconDown = svg(<path d="M6 9l6 6 6-6" />);
export const IconLeft = svg(<path d="M15 18l-6-6 6-6" />);
export const IconRight = svg(<path d="M9 18l6-6-6-6" />);
export const IconPencil = svg(
  <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></>,
);
export const IconHistory = svg(
  <><path d="M3 12a9 9 0 106-8.5" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></>,
);
export const IconCheck = svg(<path d="M20 6L9 17l-5-5" />);
export const IconAlert = svg(
  <>
    <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
    <path d="M12 9v4" /><path d="M12 17h.01" />
  </>,
);
export const IconX = svg(<><path d="M18 6L6 18" /><path d="M6 6l12 12" /></>);
export const IconCalendar = svg(
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
  </>,
);
export const IconColumns = svg(
  <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M12 4v16" /></>,
);
export const IconSettings = svg(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.2 2.2M16.9 16.9l2.2 2.2M19.1 4.9l-2.2 2.2M7.1 16.9l-2.2 2.2" />
  </>,
);
/** Info tone marker for callouts — a plain circle-i, distinct from `IconAlert`'s
 *  triangle and `IconOctagon`'s stop-sign so the three callout tones read apart
 *  by SHAPE, not only colour. */
export const IconInfo = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" /><path d="M12 8h.01" />
  </>,
);
/** Danger tone marker for callouts — an octagon (the "stop sign" silhouette),
 *  paired with `IconAlert`'s triangle for warning so the two most-confusable
 *  tones (amber vs red) are also shaped differently. */
export const IconOctagon = svg(
  <>
    <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z" />
    <path d="M12 8v5" /><path d="M12 16h.01" />
  </>,
);

/* ── status tags ────────────────────────────────────────────────────────── */

export type TagTone =
  | 'published' | 'active' | 'draft' | 'review' | 'approved'
  | 'scheduled' | 'retired' | 'material' | 'minor';

const TAG_TONES: Record<TagTone, string> = {
  published: 'bg-emerald-500/15 text-emerald-400',
  active: 'bg-emerald-500/15 text-emerald-400',
  draft: 'bg-stone-100 text-stone-400',
  minor: 'bg-stone-100 text-stone-400',
  review: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-blue-500/15 text-blue-300',
  scheduled: 'bg-violet-500/15 text-violet-400',
  retired: 'bg-red-500/10 text-red-300',
  material: 'bg-red-500/15 text-red-300',
};

export function Tag({
  tone, children, className = '',
}: { tone: TagTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold tracking-wide ${TAG_TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/* ── emerald pill tabs ──────────────────────────────────────────────────── */

export interface PillTab {
  id: string;
  label: string;
  icon?: ReactNode;
  count?: number;
}

/** Platform standard: emerald pill when active, outlined when not, icon on the left. */
export function PillTabs({
  tabs, value, onChange, className = '',
}: {
  tabs: readonly PillTab[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} role="tablist">
      {tabs.map((t) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.id)}
            className={`flex min-h-[44px] items-center gap-2 rounded-full border px-4 text-[13px] transition-colors ${
              on
                ? 'border-emerald-500 bg-emerald-500 font-semibold text-emerald-950'
                : 'border-stone-200 bg-transparent text-stone-400 hover:border-stone-300 hover:text-stone-900'
            }`}
          >
            {t.icon}
            <span>{t.label}</span>
            {typeof t.count === 'number' && (
              <span
                className={`rounded-full px-1.5 text-[10px] ${
                  on ? 'bg-emerald-900/25 text-emerald-950' : 'bg-stone-100 text-stone-400'
                }`}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── notices ────────────────────────────────────────────────────────────── */

export function WarnNote({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed text-amber-300 ${className}`}
    >
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export function DangerNote({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-3 text-xs leading-relaxed text-red-300 ${className}`}
    >
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

/* ── modal shell ────────────────────────────────────────────────────────── */

/**
 * Overlay + card. Rendered inline rather than through a portal so this package
 * never imports `react-dom` — that is the surface where the React 18 / 19 split
 * between the two consuming apps actually bites.
 */
export function ModalShell({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-xl border border-stone-200 bg-white shadow-2xl"
      >
        <div className="flex items-start gap-3 border-b border-dark-700 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-900"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-dark-700 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── buttons ────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'default' | 'primary' | 'admin' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  default: 'border-stone-200 bg-stone-100 text-stone-900 hover:bg-zinc-700',
  primary: 'border-emerald-500 bg-emerald-500 font-semibold text-emerald-950 hover:bg-emerald-400',
  // `admin` is the purple super-admin accent. Spelled with a default-palette
  // violet rather than the `admin-*` token, which only exists in super-admin-ui.
  admin: 'border-violet-600 bg-violet-600 font-semibold text-stone-900 hover:bg-violet-500',
  danger: 'border-red-500/50 bg-transparent text-red-300 hover:bg-red-500/10',
};

export type ButtonProps = {
  variant?: ButtonVariant;
  className?: string;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export function Button({ variant = 'default', className = '', children, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-4 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

/** Shared input chrome — 44px minimum, dark, focus ring in emerald. */
export const INPUT_CLASS =
  'w-full min-h-[44px] rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder:text-stone-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

/** Uppercase field label used across the modal and the block editor. */
export function FieldLabel({
  children, requirement, htmlFor,
}: { children: ReactNode; requirement?: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-[11.5px] font-semibold uppercase tracking-wider text-stone-500"
    >
      {children}
      {requirement && (
        <span className="ml-1.5 normal-case tracking-normal text-red-300">{requirement}</span>
      )}
    </label>
  );
}
